import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { FaChevronLeft } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import MeritoButton from "../../MeritoButtons/MeritoButtons";
import TallySync from "../TallySync/TallySync";
import TallySyncInstall from "../TallySyncInstall/TallySyncInstall";
import TallySyncReceiving from "../TallySyncReceiving/TallySyncReceiving";
import TallySyncSuccess from "../TallySyncSuccess/TallySyncSuccess";
import styles from "./TallyDashboardSyncModal.module.scss";

export type TallyDashboardModalStage =
  | "setup"
  | "install"
  | "receiving"
  | "success";

interface TallyDashboardSyncModalProps {
  stage: TallyDashboardModalStage;
  failed: boolean;
  receivingStartedAt: number | null;
  onClose: () => void;
  onContinue: () => void;
  onBack: () => void;
  onDownload: () => Promise<void>;
}

const modalWidth = 1080;
const modalHeight = 556;
const modalViewportGap = 32;

const stageLabels: Record<TallyDashboardModalStage, string> = {
  setup: "Set Up Automatic Sync",
  install: "Install the Connector in Tally",
  receiving: "Syncing from Tally",
  success: "Data Sync Successful",
};

function getModalScale() {
  if (typeof window === "undefined") return 1;

  return Math.min(
    (window.innerWidth - modalViewportGap) / modalWidth,
    (window.innerHeight - modalViewportGap) / modalHeight,
    1
  );
}

export default function TallyDashboardSyncModal({
  stage,
  failed,
  receivingStartedAt,
  onClose,
  onContinue,
  onBack,
  onDownload,
}: TallyDashboardSyncModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [scale, setScale] = useState(getModalScale);

  useEffect(() => {
    const updateScale = () => setScale(getModalScale());
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    window.addEventListener("resize", updateScale);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("resize", updateScale);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && stage !== "receiving") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, stage]);

  const footer = (
    leftAction?: ReactNode,
    primaryAction?: ReactNode,
    showClose = true
  ) => (
    <footer className={styles.footer}>
      <div className={styles.footerLeft}>{leftAction}</div>
      <div className={styles.footerRight}>
        {showClose && (
          <MeritoButton
            type="secondary-2"
            size="lg"
            isRounded
            className={styles.outlineButton}
            extraStyles={{ padding: "0.75rem" }}
            onClick={onClose}
            btnText="Close"
          />
        )}
        {primaryAction}
      </div>
    </footer>
  );

  const stageContent = (() => {
    if (stage === "setup") {
      return (
        <TallySync
          isDashboard
          onDashboardDownload={onDownload}
        />
      );
    }

    if (stage === "install") {
      return (
        <TallySyncInstall
          isDashboard
          phase={failed ? "failed" : "idle"}
        />
      );
    }

    if (stage === "receiving") {
      return (
        <TallySyncReceiving
          isDashboard
          receivingStartedAt={receivingStartedAt}
        />
      );
    }

    return <TallySyncSuccess isDashboard />;
  })();

  const stageFooter = (() => {
    if (stage === "setup") {
      return footer(
        undefined,
        <MeritoButton
          type="primary-1"
          size="lg"
          isRounded
          className={styles.primaryButton}
          extraStyles={{ padding: "0.75rem" }}
          onClick={onContinue}
          btnText="Continue"
        />
      );
    }

    if (stage === "install") {
      return footer(
        <MeritoButton
          type="tertiary"
          size="lg"
          className={styles.backButton}
          extraStyles={{ padding: "0.75rem" }}
          onClick={onBack}
        >
          <FaChevronLeft aria-hidden="true" />
          Back
        </MeritoButton>
      );
    }

    if (stage === "receiving") {
      return (
        <footer
          className={`${styles.footer} ${styles.footerNoDivider}`}
          aria-hidden="true"
        />
      );
    }

    return footer();
  })();

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(event) => {
        if (stage !== "receiving" && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={styles.scaledFrame}
        style={{ width: modalWidth * scale, height: modalHeight * scale }}
      >
        <section
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-label={stageLabels[stage]}
          style={{ transform: `scale(${scale})` }}
        >
          {stage !== "receiving" && (
            <MeritoButton
              ref={closeButtonRef}
              type="tertiary"
              size="sm"
              onlyIcon
              isRounded
              className={styles.closeIconButton}
              extraStyles={{ padding: "0.5rem" }}
              onClick={onClose}
            >
              <MdClose aria-hidden="true" />
              <span className={styles.visuallyHidden}>Close Tally sync</span>
            </MeritoButton>
          )}

          <div className={styles.modalBody}>{stageContent}</div>
          {stageFooter}
        </section>
      </div>
    </div>,
    document.body
  );
}
